import { useRef } from 'react';

import UnitCheckGroup from '@/components/controls/UnitCheckGroup';
import InlineEdit from '@/components/InlineEdit';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { YES_NO_BASIC_OPTIONS } from '@/constants';
import { useControlContext } from '@/contexts/ControlContext';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { cn } from '@/lib/utils';


const InformationTable = () => {
  const { form, readOnly, setFormByKey } = useControlContext();
  const { informationDocument } = form;

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-64">Document Number</TableHead>
            <TableHead>Document Title</TableHead>
            <TableHead className="w-36">Current Revision</TableHead>
            <TableHead className="w-36">New Revision</TableHead>
            <TableHead className="w-64">Type of Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>
              <InlineEdit
                disabled={readOnly}
                onChange={(value) => {
                  setFormByKey('informationDocument', {
                    ...informationDocument,
                    number: value,
                  });
                }}
                placeholder="-"
                value={informationDocument.number}
              />
            </TableCell>
            <TableCell>
              <InlineEdit
                className="cursor-not-allowed"
                disabled
                placeholder="-"
              />
            </TableCell>
            <TableCell>
              <InlineEdit
                className="cursor-not-allowed"
                disabled
                placeholder="-"
              />
            </TableCell>
            <TableCell>
              <InlineEdit
                disabled={readOnly}
                onChange={(value) => {
                  setFormByKey('informationDocument', {
                    ...informationDocument,
                    newRevision: value,
                  });
                }}
                placeholder="-"
                value={informationDocument.newRevision}
              />
            </TableCell>
            <TableCell>
              <InlineEdit
                disabled={readOnly}
                onChange={(value) => {
                  setFormByKey('informationDocument', {
                    ...informationDocument,
                    type: value,
                  });
                }}
                placeholder="-"
                value={informationDocument.type}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

const InformationReason = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { informationReason } = form;

  const handleChange = ({ target }) =>
    setFormByKey('informationReason', target.value);

  return (
    <Card className="flex mb-4">
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Reason for Change Control
          </CardTitle>
          <CardDescription className="grow">
            {mode === DETAIL_MODE.STATIC ? (
              informationReason
            ) : (
              <Textarea
                autoResize
                className="font-mono resize-none"
                disabled={readOnly}
                onChange={handleChange}
                placeholder="Click here to add reasons"
                value={informationReason}
              />
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const InformationAffectedUnit = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { readOnly } = useControlContext();

  return (
    <Card className="flex mb-4">
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Affected Department/Unit
          </CardTitle>
          <CardDescription className="grow">
            <UnitCheckGroup
              disabled={mode === DETAIL_MODE.STATIC || readOnly}
              name="informationUnits"
            />
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const InformationReadWithin = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { informationReadWithin } = form;

  const dayList = Array.from({ length: 30 }).map((_, n) => n + 1);

  const handleChange = ({ target }) =>
    setFormByKey('informationReadWithin', target.value);

  return (
    <Card className="flex mb-4">
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Read and Understood (R&U) by all affected parties within:
          </CardTitle>
          <CardDescription className="grow">
            {mode === DETAIL_MODE.STATIC ? (
              <span>{informationReadWithin} days</span>
            ) : (
              <div className="flex items-center gap-2 w-1/2">
                <Select
                  disabled={readOnly}
                  onChange={handleChange}
                  value={informationReadWithin}
                >
                  {informationReadWithin === '' && <option value="">-</option>}
                  {dayList.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                <span className="shrink-0 text-sm">days</span>
              </div>
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const InformationAdditionalRequired = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { trainingRequired } = form;

  const handleChange = ({ target }) =>
    setFormByKey('trainingRequired', Number(target.value));

  return (
    <Card className="flex mb-4">
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Additional Training Required?
          </CardTitle>
          <CardDescription className="grow">
            {mode === DETAIL_MODE.STATIC ? (
              trainingRequired === 1 ? (
                'Yes'
              ) : (
                'No'
              )
            ) : (
              <Select
                disabled={readOnly}
                onChange={handleChange}
                value={trainingRequired}
              >
                {trainingRequired === '' && <option value="">-</option>}
                {YES_NO_BASIC_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const InformationRequiredAttendees = ({ disabled }) => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { readOnly } = useControlContext();

  return (
    <Card
      className={cn('flex mb-4', disabled && 'opacity-50 pointer-events-none')}
    >
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Required Attendees
          </CardTitle>
          <CardDescription className="grow">
            <UnitCheckGroup
              disabled={mode === DETAIL_MODE.STATIC || readOnly}
              name="trainingAttendees"
            />
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const InformationDeadline = ({ disabled }) => {
  const dateRef = useRef(null);

  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { trainingDeadline } = form;

  const handleChange = ({ target }) =>
    setFormByKey('trainingDeadline', target.value);

  const handleShowPicker = () => dateRef.current?.showPicker();

  return (
    <Card
      className={cn('flex mb-4', disabled && 'opacity-50 pointer-events-none')}
    >
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Training Deadline:
          </CardTitle>
          <CardDescription className="grow">
            {mode === DETAIL_MODE.STATIC ? (
              trainingDeadline
            ) : (
              <Input
                disabled={readOnly}
                maxLength={10}
                onChange={handleChange}
                placeholder="dd/mm/yyyy"
                type="date"
                onClick={handleShowPicker}
                ref={dateRef}
                value={trainingDeadline}
              />
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const InformationAdditionalNote = ({ disabled }) => {
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form, readOnly, setFormByKey } = useControlContext();
  const { trainingInformation } = form;

  const handleChange = ({ target }) =>
    setFormByKey('trainingInformation', target.value);

  return (
    <Card
      className={cn('flex mb-4', disabled && 'opacity-50 pointer-events-none')}
    >
      <CardHeader className="pb-6 grow">
        <div className="flex gap-4 items-start justify-between">
          <CardTitle className="text-base font-medium w-1/4">
            Additional Training Information
          </CardTitle>
          <CardDescription className="grow">
            {mode === DETAIL_MODE.STATIC ? (
              trainingInformation
            ) : (
              <Textarea
                className="font-mono resize-none"
                disabled={readOnly}
                onChange={handleChange}
                placeholder="Click to describe the training to be conducted (e.g. face to face onsite training, role play)"
                rows={4}
                value={trainingInformation}
              />
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
};

const ControlInformationView = () => {
  const { form } = useControlContext();
  const trainingRequired = form.trainingRequired === 1;

  return (
    <div className="space-y-4">
      {false && <InformationTable />}
      <InformationReason />
      <InformationAffectedUnit />
      <InformationReadWithin />
      <InformationAdditionalRequired />
      {trainingRequired && <InformationRequiredAttendees />}
      {trainingRequired && <InformationDeadline />}
      {trainingRequired && <InformationAdditionalNote />}
    </div>
  );
};

export default ControlInformationView;
